import { Box, Button, Grid } from '@mui/material';
import { forwardRef, useImperativeHandle } from 'react';
import { Controller } from 'react-hook-form';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { Progress } from 'src/components/progress';
import { InputWidget, SelectDropdownWidget } from 'src/components/widget';
import { generateOptions, lang } from 'src/utils';

import { useComponentScheduleDrawer } from './ComponentScheduleDrawer.hook';
import { ComponentScheduleDrawerProps } from './ComponentScheduleDrawer.interface';
import {
  ButtonWrapper,
  FixedWidthColumn,
  FormCheckbox,
  FormControlLabel,
  Header,
  HeaderText,
} from './ComponentScheduleDrawer.styled';

export const ComponentScheduleDrawer = forwardRef(
  (props: ComponentScheduleDrawerProps, ref) => {
    const {
      formMethods,
      isLoading,
      activeDaypartOptions,
      handleFormSubmit,
      emptyScheduleNameValidate,
      onHandleCloseDrawer,
    } = useComponentScheduleDrawer(props);

    useImperativeHandle(ref, () => ({
      onHandleCloseDrawer,
    }));

    return (
      <FixedWidthColumn>
        <Header>
          <HeaderText>{lang.scheduleComponent.label}</HeaderText>
        </Header>

        <Box>
          {isLoading ? (
            <Progress />
          ) : (
            <form onSubmit={formMethods.handleSubmit(handleFormSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {props.isOpen && (
                    <Controller
                      name="scheduleName"
                      control={formMethods.control}
                      rules={{ validate: emptyScheduleNameValidate }}
                      render={({ field, fieldState }) => (
                        <InputWidget
                          {...field}
                          placeholder={lang.scheduleComponent.name}
                          label={lang.scheduleComponent.scheduleNameField}
                          hasError={!!fieldState.error}
                          error={fieldState.error?.message || false}
                          onChange={(value) => {
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="selectedDaypartId"
                    control={formMethods.control}
                    rules={{ required: lang.daypartRequired }}
                    render={({ field, fieldState }) => (
                      <SelectDropdownWidget
                        {...field}
                        hasError={!!fieldState.error}
                        error={fieldState.error?.message || false}
                        label={lang.daypart}
                        selectOptions={generateOptions(
                          activeDaypartOptions,
                          'name',
                          'id',
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputTextLabel>
                    {lang.scheduleComponent.additionalLocationGroupsOptional}
                  </InputTextLabel>
                  <FormControlLabel
                    control={
                      <FormCheckbox
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) =>
                          formMethods.setValue(
                            'additionalLocationGroups',
                            event.target.checked,
                          )
                        }
                      />
                    }
                    label={lang.scheduleComponent.scheduleMultiplLocation}
                  />
                </Grid>
              </Grid>

              <ButtonWrapper>
                <Button
                  fullWidth={true}
                  variant="contained"
                  disabled={isLoading}
                  type="submit"
                  onSubmit={() => handleFormSubmit}>
                  {lang.scheduleComponent.label}
                </Button>
              </ButtonWrapper>
            </form>
          )}
        </Box>
      </FixedWidthColumn>
    );
  },
);
